import React, { useState, useEffect } from 'react';

const COOKIE_NAME = 'orderflow_cookie_consent';
const COOKIE_EXPIRY = 365;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${value};expires=${expires};path=/;SameSite=Strict;Secure`;
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const consent = getCookie(COOKIE_NAME);
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1500);
    }
  }, []);

  const acceptAll = () => {
    setCookie(COOKIE_NAME, 'accepted', COOKIE_EXPIRY);
    setShowBanner(false);
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      });
    }
  };

  const acceptNecessary = () => {
    setCookie(COOKIE_NAME, 'necessary_only', COOKIE_EXPIRY);
    setShowBanner(false);
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
    }
  };

  if (!showBanner) return null;

  return (
    <div 
      role="dialog"
      aria-label="Cookie consent"
      className="fixed bottom-4 right-4 left-4 md:left-auto z-30 w-auto md:max-w-sm"
      style={{
        animation: 'slideUp 0.4s ease-out'
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center">
              <svg className="w-5 h-5 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900 mb-1">
                We value your privacy
              </h2>
              <p className="text-xs text-gray-600">
                We use cookies to improve your experience.{' '}
                <button 
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-gold hover:underline font-medium ml-1"
                >
                  {isExpanded ? 'Less' : 'More'}
                </button>
              </p>
            </div>
          </div>

          <div 
            className="overflow-hidden transition-all duration-200"
            style={{
              maxHeight: isExpanded ? '60px' : '0',
              opacity: isExpanded ? 1 : 0,
              marginTop: isExpanded ? '12px' : '0'
            }}
          >
            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Essential & Analytics cookies
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={acceptNecessary}
              className="flex-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-all focus:outline-none focus:ring-2 focus:ring-gold"
            >
              Decline
            </button>
            <button
              onClick={acceptAll}
              className="flex-1 px-3 py-2 text-xs font-medium text-white bg-gradient-to-r from-[#d4af37] to-[#b8962e] hover:brightness-110 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-gold shadow-md"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { CookieConsent, getCookie, setCookie };
export default CookieConsent;